import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getData, postData } from '../middleware/apiMiddleware';
import s from './KeyComplexSentencePanel.module.css';

const DIFFICULTY_OPTIONS = [
	'Advanced', 'Bridging', 'Bridging-Level-1', 'Bridging-Level-2', 'Bridging-Level-3',
	'DSE-essential', 'DSE-intermediate+', 'Essential', 'IB-MYP-Y2-3', 'Intermediate+', 'Pre-DSE-Simplified', 
	'Pre-DSE', 'Pre-DSE-developing', 'Pre-DSE-foundation'
];

const BATCH_DIFFICULTY_OPTIONS = ['Advanced', 'Intermediate+', 'Essential', 'Pre-DSE'];
const REQUIRED_OBJ_KEYS = ['original_sentence', 'caption', 'grammar_trait', 'key_structural_focus'];

const GEN_AI_ACTIONS = [
    { label: 'Regenerate ALL', api: 'genAi', payload: { force: true } },
    { label: 'Recover ALL', api: 'genAi', payload: { force: false } },
    { label: 'Vocabulary', api: 'genCSVocabulary', payload: {} },
    { label: 'Parse Tree', api: 'genCSParseTree', payload: {} },
    { label: 'Sentence Structure', api: 'genCSSentenceStructure', payload: {} },
    { label: 'Question', api: 'genCSQuestion', payload: {} },
];

const NULL_FILTER_VALUE = '_NULL_';
const UNGROUPED_KEY = '_UNGROUPED_';

export default function KeyComplexSentencePanel() {
	const navigate = useNavigate();
	const [sentences, setSentences] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [editingCell, setEditingCell] = useState(null);
	const [processingItemId, setProcessingItemId] = useState(null);

	// Editing States
	const [editingName, setEditingName] = useState('');
	const [editingNameHk, setEditingNameHk] = useState('');
	const [editingMainSentence, setEditingMainSentence] = useState('');
	const [editingGrammarTrait, setEditingGrammarTrait] = useState('');
	const [editingKeyClauseType, setEditingKeyClauseType] = useState('');

	// Filter States
	const [selectedDifficulty, setSelectedDifficulty] = useState('');
	const [nameFilter, setNameFilter] = useState('');
	const [selectedStatus, setSelectedStatus] = useState('');

	// Create Modal States for Batch Creation
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [newBatchDifficulty, setNewBatchDifficulty] = useState(BATCH_DIFFICULTY_OPTIONS[0]);
	const [newBatchGroupName, setNewBatchGroupName] = useState('');
	const [newBatchJson, setNewBatchJson] = useState('');
	const [parsedObjs, setParsedObjs] = useState([]);
	const [jsonError, setJsonError] = useState(null);

	// State for Collapsible Groups
	const [collapsedGroups, setCollapsedGroups] = useState(new Set());

	useEffect(() => {
		if (!newBatchJson.trim()) {
			setParsedObjs([]);
			setJsonError(null);
			return;
		}

		try {
			const parsed = JSON.parse(newBatchJson);
 
			if (!Array.isArray(parsed)) {
				throw new Error('The provided JSON is not an array.');
			}
 
			if (parsed.length === 0) {
				throw new Error('The JSON array cannot be empty.');
			}

			// Validate each object in the array
			for (let i = 0; i < parsed.length; i++) {
				const obj = parsed[i];
				if (typeof obj !== 'object' || obj === null) {
					throw new Error(`Item at index ${i} is not a valid object.`);
				}
				for (const key of REQUIRED_OBJ_KEYS) {
					if (!(key in obj)) {
						throw new Error(`Item at index ${i} is missing the required key: "${key}".`);
					}
					if (typeof obj[key] !== 'string') {
						throw new Error(`The value for "${key}" at index ${i} must be a string.`);
					}
				}
			}

			setParsedObjs(parsed);
			setJsonError(null); // Clear previous errors

		} catch (e) {
			setParsedObjs([]);
			setJsonError(e.message);
		}
	}, [newBatchJson]);

	const fetchList = useCallback(() => {
		setIsLoading(true);
		getData(`item/listKeyComplexSentence`, navigate)
			.then(res => {
				if (res && res.data) {
					setSentences(res.data);
				} else {
					setSentences([]);
				}
			})
			.catch(err => {
				console.error("Failed to fetch complex sentences:", err);
				setError("Failed to load complex sentences. Please try again.");
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [navigate]);

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	const resetCreateModalState = () => {
		setNewBatchDifficulty(BATCH_DIFFICULTY_OPTIONS[0]);
		setNewBatchGroupName('');
		setNewBatchJson('');
		setParsedObjs([]);
		setJsonError(null);
		setIsCreating(false);
	};

	const handleBatchCreate = async () => {
		if (parsedObjs.length === 0 || jsonError) {
			alert("Please provide valid JSON data for the items.");
			return;
		}

		setIsCreating(true);
		const payload = {
			difficulty: newBatchDifficulty,
			groupName: newBatchGroupName.trim() || null,
			objs: parsedObjs
		};
 
		try {
			await postData("item/createComplexSentences", payload, navigate);
			alert(`Successfully created ${parsedObjs.length} items.`);
			setIsCreateModalOpen(false);
			resetCreateModalState();
			fetchList();
		} catch (error) {
			console.error("Failed during batch item creation:", error);
			alert("An error occurred during creation. Please check the server logs and try again.");
		} finally {
			setIsCreating(false);
		}
	};

  const groupedAndFilteredSentences = useMemo(() => {
		const trimmedNameFilter = nameFilter.trim().toLowerCase();
 
		const filtered = sentences.filter(sentence => {
			let difficultyMatch = true;
			if (selectedDifficulty === NULL_FILTER_VALUE) difficultyMatch = sentence.difficulty === null;
			else if (selectedDifficulty) difficultyMatch = sentence.difficulty === selectedDifficulty;

			const nameMatch = trimmedNameFilter
				? (sentence.name || '').toLowerCase().includes(trimmedNameFilter) ||
				 (sentence.name_hk || '').toLowerCase().includes(trimmedNameFilter)
				: true;

			let statusMatch = true;
			if (selectedStatus) {
				statusMatch = sentence.is_published === (selectedStatus === 'true');
			}

			return difficultyMatch && nameMatch && statusMatch;
		});

		return filtered.reduce((acc, sentence) => {
			const groupKey = sentence.related_name || UNGROUPED_KEY;
			if (!acc[groupKey]) {
				acc[groupKey] = [];
			}
			acc[groupKey].push(sentence);
			return acc;
		}, {});

	}, [sentences, selectedDifficulty, nameFilter, selectedStatus]);
  
    const handleToggleGroup = (groupName) => {
        setCollapsedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupName)) {
                newSet.delete(groupName);
            } else {
                newSet.add(groupName);
            }
            return newSet;
        });
    };

    const handleGenAiAction = async (itemId, api, payload = {}, actionName) => {
		setSentences(current =>
			current.map(s => s.item_id === itemId ? { ...s, status: 'generating' } : s)
		);
       
        const finalPayload = { itemId: String(itemId), ...payload };
        try {
            postData(`complexSentenceApi/${api}`, finalPayload, navigate)
				.then((response) => {
					setSentences(current =>
						current.map(s => s.item_id === itemId ? { ...s, status: 'completed' } : s)
					);
				})
				.catch((error) => {
				});      
        } catch (error) {
            console.error(`AI Generation for action '${actionName}' failed:`, error);
            alert(`Failed to start AI action '${actionName}'. Please try again.`);
        }
    };
  
    const handleCancelGenAiClick = (itemId) => {
		const payload = { itemId };
		postData("complexSentenceApi/clearCSFlag", payload, navigate);
        setSentences(current => 
            current.map(s => s.item_id === itemId ? { ...s, status: 'pending' } : s)
        );
    };
  
	const handleDifficultyUpdate = async (itemId, newDifficulty) => {
		const sentenceToUpdate = sentences.find(s => s.item_id === itemId);
		if (!sentenceToUpdate) return;
		setEditingCell(null);

		const valueToSend = newDifficulty === "" ? null : newDifficulty;
		const payload = { itemId: String(itemId), difficulty: valueToSend };

		try {
			await postData("item/updateKeyDifficulty", payload, navigate);
			setSentences(current =>
				current.map(s => (s.item_id === itemId ? { ...s, difficulty: valueToSend } : s))
			);
		} catch (error) {
			console.error("Difficulty update failed:", error);
			alert("Difficulty update failed.");
		}
	};
	
	const handleNameUpdate = async (itemId, newName) => {
		const itemToUpdate = sentences.find(s => s.item_id === itemId);
		if (!itemToUpdate || itemToUpdate.name === newName || !newName.trim()) {
			setEditingCell(null);
			return;
		}
		setEditingCell(null);
		const payload = { itemId: String(itemId), name: newName.trim() };
		try {
			await postData("item/updateName", payload, navigate);
			setSentences(current =>
				current.map(s => (s.item_id === itemId ? { ...s, name: payload.name } : s))
			);
		} catch (error) { console.error("Name update failed:", error); alert("Name update failed."); }
	};

	const handleNameHkUpdate = async (itemId, newNameHk) => {
		const itemToUpdate = sentences.find(s => s.item_id === itemId);
		const trimmedNameHk = newNameHk ? newNameHk.trim() : null;
		if (!itemToUpdate || itemToUpdate.name_hk === trimmedNameHk) {
			setEditingCell(null);
			return;
		}
		setEditingCell(null);
		const payload = { itemId: String(itemId), nameHk: trimmedNameHk };
		try {
			await postData("item/updateNameHk", payload, navigate);
			setSentences(current =>
				current.map(s => (s.item_id === itemId ? { ...s, name_hk: payload.nameHk } : s))
			);
		} catch (error) { console.error("Name (HK) update failed:", error); alert("Name (HK) update failed."); }
	};

	const handleComplexSentenceUpdate = async (itemId) => {
		if (editingCell?.itemId !== itemId) return;
 
		setEditingCell(null);
		const sentenceToUpdate = sentences.find(s => s.item_id === itemId);
		if (!sentenceToUpdate) return;
	
		const payload = {
			itemId: String(itemId),
			data: {
				main_sentence: editingMainSentence,
				grammar_trait: editingGrammarTrait,
				key_clause_type: editingKeyClauseType
			}
		};

		if (sentenceToUpdate.main_sentence === payload.data.main_sentence &&
			sentenceToUpdate.grammar_trait === payload.data.grammar_trait &&
			sentenceToUpdate.key_clause_type === payload.data.key_clause_type) {
			return;
		}

		try {
			await postData("complexSentenceApi/updateComplexSentence", payload, navigate);
			setSentences(current =>
				current.map(s => (s.item_id === itemId ? { ...s, ...payload.data } : s))
			);
		} catch (error) {
			console.error("Complex sentence update failed:", error);
			alert("Complex sentence update failed.");
		}
	};

	const handlePublishToggle = async (itemId, currentStatus) => {
		const newStatus = !currentStatus;
		const payload = { itemId: String(itemId), isPublished: newStatus };

		setSentences(current => current.map(s => s.item_id === itemId ? { ...s, is_published: newStatus } : s));

		try {
			await postData("item/publish", payload, navigate);
		} catch (error) {
			console.error("Publish status update failed:", error);
			alert("Failed to update publish status.");
			setSentences(current => current.map(s => s.item_id === itemId ? { ...s, is_published: currentStatus } : s));
		}
	};

	const handleKeyDown = (e, handler, ...args) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault(); 
			handler(...args);
		}
		else if (e.key === 'Escape') setEditingCell(null);
	};

	const startEditingComplex = (sentence) => {
		setEditingCell({ itemId: sentence.item_id, field: 'complexData' });
		setEditingMainSentence(sentence.main_sentence || '');
		setEditingGrammarTrait(sentence.grammar_trait || '');
		setEditingKeyClauseType(sentence.key_clause_type || '');
	};
  
    const statusClassMap = {
        pending: s.statusPending,
        generating: s.statusGenerating,
    };

	return (
		<div className={s.panelContainer}>
			{isCreateModalOpen && (
				<div className={s.modalBackdrop}>
					<div className={s.modalContent}>
						<h3>Batch Create Complex Sentences</h3>
						<div className={s.modalBody}>
							<div className={s.modalFormGroup}>
								<label htmlFor="batch-difficulty">Difficulty:</label>
								<select id="batch-difficulty" value={newBatchDifficulty} onChange={(e) => setNewBatchDifficulty(e.target.value)} className={s.modalSelect}>
									{BATCH_DIFFICULTY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
								</select>
							</div>

							<div className={s.modalFormGroup}>
								<label htmlFor="batch-groupname">Group Name (Optional):</label>
								<input
									id="batch-groupname"
									type="text"
									className={s.modalInput}
									value={newBatchGroupName}
									onChange={(e) => setNewBatchGroupName(e.target.value)}
									placeholder="e.g., AI Companions II"
								/>
							</div>

							<div className={s.modalFormGroup}>
								<label htmlFor="batch-json">Paste JSON data for `objs` array:</label>
								<textarea
									id="batch-json"
									className={`${s.modalTextarea} ${s.jsonTextarea} ${jsonError ? s.textareaError : ''}`}
									value={newBatchJson}
									onChange={(e) => setNewBatchJson(e.target.value)}
									placeholder='[{"original_sentence": "...", "caption": "...", "grammar_trait": "...", "key_structural_focus": "..."}]'
									autoFocus
								/>
								{jsonError && <p className={s.modalErrorText}>{jsonError}</p>}
								{!jsonError && parsedObjs.length > 0 && <p className={s.modalSuccessText}>Successfully parsed {parsedObjs.length} items.</p>}
							</div>
						</div>
						<div className={s.modalFooter}>
							<button className={s.modalButton} onClick={() => { setIsCreateModalOpen(false); resetCreateModalState(); }} disabled={isCreating}>Cancel</button>
							<button className={`${s.modalButton} ${s.confirmButton}`} onClick={handleBatchCreate} disabled={isCreating || parsedObjs.length === 0 || !!jsonError}>
								{isCreating ? 'Creating...' : `Create (${parsedObjs.length}) Items`}
							</button>
						</div>
					</div>
				</div>
			)}

			<div className={s.filterContainer}>
				<button className={s.createButton} onClick={() => setIsCreateModalOpen(true)}>Create Batch (93)</button>
				<div className={s.filterGroup}>
					<label htmlFor="name-filter">Filter by Name:</label>
					<input id="name-filter" type="text" placeholder="Enter Name or Name (HK)..." value={nameFilter} onChange={e => setNameFilter(e.target.value)} className={s.filterInput}/>
				</div>
				<div className={s.filterGroup}>
					<label htmlFor="status-filter">Status:</label>
					<select id="status-filter" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
						<option value="">All</option>
						<option value="true">Published</option>
						<option value="false">Unpublished</option>
					</select>
				</div>
				<div className={s.filterGroup}>
					<label htmlFor="difficulty-filter">Difficulty:</label>
					<select id="difficulty-filter" value={selectedDifficulty} onChange={e => setSelectedDifficulty(e.target.value)}>
						<option value="">All</option>
						<option value={NULL_FILTER_VALUE}>(NULL)</option>
						{DIFFICULTY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
					</select>
				</div>
			</div>

			{isLoading && <p>Loading complex sentences...</p>}
			{error && <p className={s.errorMessage}>Error: {error}</p>}

        {!isLoading && !error && (
				<div className={s.listContainer}>
					<div className={s.headerRow}>
						<div className={`${s.column} ${s.colId}`}>ID</div>
						<div className={`${s.column} ${s.colPublished}`}>Published</div>
						<div className={`${s.column} ${s.colName}`}>Name</div>
						<div className={`${s.column} ${s.colNameHk}`}>Name (HK)</div>
						<div className={`${s.column} ${s.colDifficulty}`}>Difficulty</div>
						<div className={`${s.column} ${s.colMainSentence}`}>Main Sentence</div>
						<div className={`${s.column} ${s.colGrammarTrait}`}>Grammar Trait</div>
						<div className={`${s.column} ${s.colKeyClause}`}>Key Clause Type</div>
						<div className={`${s.column} ${s.colActions}`}>Actions</div>
					</div>
					<div className={s.dataRowsContainer}>
						{Object.keys(groupedAndFilteredSentences).length > 0 ? (
							Object.entries(groupedAndFilteredSentences).map(([groupName, groupSentences]) => {
                        const isCollapsed = collapsedGroups.has(groupName);
                        return (
                        <React.Fragment key={groupName}>
                            <div className={s.groupHeaderRow} onClick={() => handleToggleGroup(groupName)}>
                                <span className={s.groupToggleIcon}>{isCollapsed ? '▶' : '▼'}</span>
                                    {groupName === UNGROUPED_KEY ? 'Ungrouped Items' : groupName}
                            </div>
                            {!isCollapsed && groupSentences.map((sentence) => {
                                const isEditingComplex = editingCell?.itemId === sentence.item_id && editingCell?.field === 'complexData';
                                const statusClassName = statusClassMap[sentence.status] || '';
                                const isCurrentlyProcessing = processingItemId === sentence.item_id;

                                return (
                                        <div key={sentence.item_id} className={s.rowWrapper}>
                                            <div className={`${s.dataRow} ${isEditingComplex ? s.isEditing : ''} ${statusClassName}`}>
											    <div className={`${s.column} ${s.colId}`}>
												    <Link to={`/generalitem/${sentence.item_id}`} target="_blank" rel="noopener noreferrer">{sentence.item_id}</Link>
											    </div>
											    <div className={`${s.column} ${s.colPublished}`}>
												    <label className={s.toggleSwitch}>
													    <input type="checkbox" checked={sentence.is_published} onChange={() => handlePublishToggle(sentence.item_id, sentence.is_published)} />
													    <span className={s.slider}></span>
												    </label>
											    </div>
											    <div className={`${s.column} ${s.colName}`}>
												    {editingCell?.itemId === sentence.item_id && editingCell?.field === 'name' ? (
													    <input type="text" className={s.inlineInput} value={editingName} onChange={(e) => setEditingName(e.target.value)} onBlur={() => handleNameUpdate(sentence.item_id, editingName)} onKeyDown={(e) => handleKeyDown(e, handleNameUpdate, sentence.item_id, editingName)} autoFocus/>
												    ) : (<span className={s.editableCell} onClick={() => { setEditingCell({ itemId: sentence.item_id, field: 'name' }); setEditingName(sentence.name); }}>{sentence.name}</span>)}
											    </div>
											    <div className={`${s.column} ${s.colNameHk}`}>
												    {editingCell?.itemId === sentence.item_id && editingCell?.field === 'name_hk' ? (
													    <input type="text" className={s.inlineInput} value={editingNameHk} onChange={(e) => setEditingNameHk(e.target.value)} onBlur={() => handleNameHkUpdate(sentence.item_id, editingNameHk)} onKeyDown={(e) => handleKeyDown(e, handleNameHkUpdate, sentence.item_id, editingNameHk)} autoFocus/>
												    ) : (<span className={s.editableCell} onClick={() => { setEditingCell({ itemId: sentence.item_id, field: 'name_hk' }); setEditingNameHk(sentence.name_hk || ''); }}>{sentence.name_hk ? sentence.name_hk : <span className={s.nullValue}>NULL</span>}</span>)}
											    </div>
											    <div className={`${s.column} ${s.colDifficulty}`}>
												    {editingCell?.itemId === sentence.item_id && editingCell?.field === 'difficulty' ? (
													    <select className={s.inlineSelect} value={sentence.difficulty || ''} onChange={(e) => handleDifficultyUpdate(sentence.item_id, e.target.value)} onBlur={() => setEditingCell(null)} autoFocus>
														    <option value="">(Set to NULL)</option>
														    {DIFFICULTY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
													    </select>
												    ) : (<span onClick={() => setEditingCell({ itemId: sentence.item_id, field: 'difficulty' })} className={s.editableCell}>{sentence.difficulty ? sentence.difficulty : <span className={s.nullValue}>NULL</span>}</span>)}
											    </div>

											    {isEditingComplex ? (
												    <>
													    <div className={`${s.column} ${s.colMainSentence}`}><textarea className={s.inlineTextarea} value={editingMainSentence} onChange={(e) => setEditingMainSentence(e.target.value)} onBlur={() => handleComplexSentenceUpdate(sentence.item_id)} onKeyDown={(e) => handleKeyDown(e, handleComplexSentenceUpdate, sentence.item_id)} autoFocus/></div>
													    <div className={`${s.column} ${s.colGrammarTrait}`}><textarea className={s.inlineTextarea} value={editingGrammarTrait} onChange={(e) => setEditingGrammarTrait(e.target.value)} onBlur={() => handleComplexSentenceUpdate(sentence.item_id)} onKeyDown={(e) => handleKeyDown(e, handleComplexSentenceUpdate, sentence.item_id)} /></div>
													    <div className={`${s.column} ${s.colKeyClause}`}><textarea className={s.inlineTextarea} value={editingKeyClauseType} onChange={(e) => setEditingKeyClauseType(e.target.value)} onBlur={() => handleComplexSentenceUpdate(sentence.item_id)} onKeyDown={(e) => handleKeyDown(e, handleComplexSentenceUpdate, sentence.item_id)} /></div>
												    </>
											    ) : (
												    <>
													    <div className={`${s.column} ${s.colMainSentence}`} onClick={() => startEditingComplex(sentence)}><span className={`${s.editableCell} ${s.truncatedText}`} title={sentence.main_sentence}>{sentence.main_sentence || <span className={s.nullValue}>NULL</span>}</span></div>
													    <div className={`${s.column} ${s.colGrammarTrait}`} onClick={() => startEditingComplex(sentence)}><span className={`${s.editableCell} ${s.truncatedText}`} title={sentence.grammar_trait}>{sentence.grammar_trait || <span className={s.nullValue}>NULL</span>}</span></div>
													    <div className={`${s.column} ${s.colKeyClause}`} onClick={() => startEditingComplex(sentence)}><span className={`${s.editableCell} ${s.truncatedText}`} title={sentence.key_clause_type}>{sentence.key_clause_type || <span className={s.nullValue}>NULL</span>}</span></div>
												    </>
											    )}
											    <div className={`${s.column} ${s.colActions}`}>
                                                    {sentence.status === 'generating' && (
                                                        <button className={`${s.actionButton} ${s.cancelButton}`} onClick={() => handleCancelGenAiClick(sentence.item_id)}>Cancel Gen AI</button>
                                                    )}
											    </div>
										    </div>
                                            {sentence.status !== 'generating' && (!sentence.is_published) && (
                                                <div className={s.genAiActionsContainer}>
                                                    {GEN_AI_ACTIONS.map(action => (
                                                        <button
                                                            key={action.label}
                                                            className={s.genAiSubButton}
                                                            onClick={() => handleGenAiAction(sentence.item_id, action.api, action.payload, action.label)}
                                                            disabled={isCurrentlyProcessing}
                                                        >
                                                            {isCurrentlyProcessing ? 'Processing...' : action.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                )
                            })}
                        </React.Fragment>
                        )
                          })
						) : (
							<p className={s.noDataMessage}>No matching items found.</p>
						)}
					</div>
				</div>
			)}
		</div>
	);
}